/** @jsxImportSource @emotion/react */
import { CountryModel } from '../../../../models';
import { ALL_COUNTRIES_LABEL } from '../../../../constants';
import { SGRocketAutocomplete } from '../../../autocomplete/SGRocketAutocomplete';
import React, { useEffect, useMemo, useState } from 'react';
import { uniqBy } from 'lodash';
import {
  countryTier1,
  countryTier2,
  countryTier3,
} from '../../../../constants/country-tiers';
import { FilterOptionsState } from '@mui/material/useAutocomplete';

interface Props {
  value: string[];
  options: CountryModel[];
  onChange: (newValues: string[]) => void;
}

const TIER_1_OPTIONS: CountryModel[] = countryTier1.map(
  (code) => ({ code }) as CountryModel,
);
const TIER_2_OPTIONS: CountryModel[] = countryTier2.map(
  (code) => ({ code }) as CountryModel,
);
const TIER_3_OPTIONS: CountryModel[] = countryTier3.map(
  (code) => ({ code }) as CountryModel,
);

export function filterLocationOptions(
  options: CountryModel[],
  { inputValue }: FilterOptionsState<CountryModel>,
): CountryModel[] {
  if (!inputValue) {
    return options;
  }

  const level1 = options.filter((option) => {
    const value = option && `${option.code} ${option.name}`;

    return new RegExp(`(^${inputValue})|(\\s${inputValue})`, 'gi').test(value);
  });

  const level2 = options.filter((option) => {
    const value = option && `${option.code} ${option.name}`;

    return new RegExp(`(${inputValue})`, 'gi').test(value);
  });

  return uniqBy([...level1, ...level2], (c) => c.code);
}

export function CountryTierAutocomplete({
  value: stringValue,
  options,
  onChange,
}: Props): JSX.Element {
  const [value, setValue] = useState([]);

  useEffect(() => {
    const selected = [...options].filter((o) =>
      stringValue.find((code) => code === o.code),
    );

    setValue(selected);
  }, [stringValue]);

  const onValueChanged = (v: CountryModel[]) => {
    const values = uniqBy(v, 'code');

    setValue(values);
    onChange(values.map((c) => c.code));
  };

  const filterTiers = (trOptions: CountryModel[]) => {
    if (!options || !options.length) {
      return [];
    }

    return trOptions.filter((trOption) => {
      const has = options.find((o) => o.code === trOption.code);

      // todo : remove it
      if (!has) {
        console.warn(trOption.code);
      }

      return !!has;
    });
  };

  const shortcutOptions = useMemo((): (CountryModel & {
    values: CountryModel[];
  })[] => {
    const TIER_1 = {
      code: 'tier1',
      name: 'Tier 1',
      values: filterTiers(TIER_1_OPTIONS),
    };
    const TIER_2 = {
      code: 'tier2',
      name: 'Tier 2',
      values: filterTiers(TIER_2_OPTIONS),
    };
    const TIER_3 = {
      code: 'tier3',
      name: 'Tier 3',
      values: filterTiers(TIER_3_OPTIONS),
    };

    return [TIER_1, TIER_2, TIER_3];
  }, [options]);

  return (
    <SGRocketAutocomplete<CountryModel>
      fullWidth
      label="Country"
      placeholder={ALL_COUNTRIES_LABEL}
      options={options}
      value={value}
      shortcutOptions={shortcutOptions}
      getOptionLabel={(option) => `${option.name}, ${option.code}`}
      getShortcutOptionLabel={(option) => option.name}
      getTagLabel={(option) => option.code}
      onChange={onValueChanged}
      isOptionEqualToValue={(option, value) => {
        return option.code === value.code;
      }}
      limitTags={5}
      filterOptions={filterLocationOptions}
    />
  );
}
